import { PERMISSIONS, isPermission } from "./permissions.js";
/**
 * The two roles defined in v1. Lives in the identity provider (WorkOS
 * today); this module mirrors the role names so app code can refer to
 * them with autocomplete. Adding a third role means: add to this union,
 * update `ROLE_PERMISSIONS`, configure in the provider, ship.
 *
 * Per FD `business-rules.md § decision rules` R1.
 */
export const ROLES = ["admin", "member"];
/**
 * Slugs that belong to the org tier / platform — never in a workspace
 * bundle. Granted by ORG roles (`ORG_ROLE_PERMISSIONS`) or the platform-staff
 * bit, not by the workspace `admin` role.
 */
const ORG_TIER_PERMISSIONS = new Set([
    "org:read",
    "org:manage",
    "org-member:read",
    "org-member:invite",
    "org-member:update-role",
    "org-member:remove",
    "billing:read",
    "billing:manage",
    "workspace:create",
    "platform:staff",
]);
/**
 * Workspace `admin` holds EVERY workspace-tier slug — derived from the
 * catalogue minus the org tier so any future slug joins `admin` automatically
 * (invariant enforced by the admin-invariant test). Replaces the hand-kept
 * literal array; `run-as` and every other new slug flow in for free.
 */
const WS_ADMIN_PERMISSIONS = PERMISSIONS.filter((p) => !ORG_TIER_PERMISSIONS.has(p));
/**
 * Maps a role to its granted permissions. The CASL ability factory reads
 * from here when assembling the per-request ability instance.
 *
 * `admin` gets every workspace-tier permission (derived). `member` gets the
 * content-CRUD subset (no member management, no api-key management, no
 * workspace settings).
 *
 * Per FD `business-rules.md § permission catalogue`.
 */
export const ROLE_PERMISSIONS = {
    admin: WS_ADMIN_PERMISSIONS,
    member: [
        "workspace:read",
        "member:read",
        "invitation:read",
        "agent:read",
        "agent:create",
        "agent:update",
        "agent:delete",
        "kb:read",
        "kb:upload",
        "kb:sync",
        "kb:attach",
        "run:read",
        "conversation:read",
        "handoff:read",
        "handoff:take-over",
        "data-table:read",
        "data-table:create",
        "data-table:update",
        "feedback:read",
        "feedback:create",
        // Workflows + stored queries (rbac-permissions PR 1) — keeps today's de
        // facto build/run capability except publish + run-as (deliberately withheld).
        "workflow:read",
        "workflow:create",
        "workflow:update",
        "workflow:delete",
        "workflow:run",
        "workflow:signal",
        "workflow:cancel",
        "query:read",
        "query:create",
        "query:update",
        "query:delete",
        "query:run",
        "run:update",
    ],
};
/**
 * Returns the permissions granted to a role. Used by session-issuance
 * code to flatten the role into the JWT claims.
 */
export function permissionsForRole(role) {
    return ROLE_PERMISSIONS[role];
}
// ---------------------------------------------------------------------------
// B1 — RBAC depth (additive). Org roles, fine-grained workspace roles, the
// platform-staff bundle, and a pure resolver that unions role tokens into a
// flat `permissions[]`. NOTHING reads these yet — Slice 2 wires the resolver
// into session issuance / the guards. See
// `aidlc-docs/construction/onboarding-connectors/proposals/b1-rbac-technical-design.md`
// §4–§6.
// ---------------------------------------------------------------------------
/**
 * Account-level roles, stored on `OrgMembership.role`. Distinct from
 * workspace roles: they govern org/billing/member-management subjects, not
 * workspace resources. Namespaced token in `roles[]`: `org:<role>`.
 */
export const ORG_ROLES = ["org_admin", "billing_admin", "member"];
/**
 * Built-in workspace roles. `admin` / `member` are the historical pair
 * (bundles untouched); `agent_editor` / `kb_manager` / `analyst` are the B1
 * fine-grained additions, recombining EXISTING catalogue slugs (no new
 * workspace slugs). Namespaced token: `ws:<role>`.
 */
export const WS_BUILTIN_ROLES = [
    "admin",
    "member",
    "agent_editor",
    "kb_manager",
    "analyst",
    // Helpdesk (M9). `helpdesk_member` operates the queue; `helpdesk_admin`
    // additionally configures it and manages the helpdesk roster. Named
    // `helpdesk_*` (not `agent`) because in this codebase `agent` already means
    // the AI agent (`agent:*`, `agent_editor`), so a human role named `agent`
    // would be permanently ambiguous. (Formerly `support_agent` / `support_lead`.)
    "helpdesk_member",
    "helpdesk_admin",
];
const ORG_ROLE_SET = new Set(ORG_ROLES);
const WS_BUILTIN_ROLE_SET = new Set(WS_BUILTIN_ROLES);
/** Type-guard: is `role` one of the three built-in org roles? */
export function isOrgRole(role) {
    return ORG_ROLE_SET.has(role);
}
/** Type-guard: is `role` one of the built-in workspace roles? */
export function isWsBuiltinRole(role) {
    return WS_BUILTIN_ROLE_SET.has(role);
}
/**
 * The built-in roles that make a member part of the HELPDESK (helpdesk-team
 * unit). Code half of the "is this a helpdesk role?" predicate — the DB half
 * is a `RoleDefinition` with `scope = 'helpdesk'`. Membership in the desk is
 * DERIVED from the role; there is no separate roster table.
 */
export const HELPDESK_BUILTIN_ROLES = ["helpdesk_admin", "helpdesk_member"];
const HELPDESK_BUILTIN_ROLE_SET = new Set(HELPDESK_BUILTIN_ROLES);
/** Type-guard: is `role` one of the built-in helpdesk roles? */
export function isHelpdeskBuiltinRole(role) {
    return HELPDESK_BUILTIN_ROLE_SET.has(role);
}
/**
 * Org-role → permission bundles (§4.1). Org roles grant org/billing/member-
 * management slugs only — never workspace resources.
 */
export const ORG_ROLE_PERMISSIONS = {
    org_admin: [
        "org:read",
        "org:manage",
        "org-member:read",
        "org-member:invite",
        "org-member:update-role",
        "org-member:remove",
        "billing:read",
        "billing:manage",
        "workspace:create",
    ],
    billing_admin: ["org:read", "billing:read", "billing:manage"],
    member: ["org:read"],
};
/**
 * The `helpdesk_member` operate bundle, shared with `helpdesk_admin` (which
 * adds the configure slugs). Kept as a named const so the two never drift.
 */
const HELPDESK_MEMBER_PERMISSIONS = [
    // NB: no `workspace:read` (that gates Workspace settings — not a desk
    // surface) and no `run:read` (AgentOps agent-run traces — not a desk
    // surface). The desk reads CONVERSATIONS, not runs.
    "conversation:monitor",
    "conversation:read",
    "handoff:read",
    "handoff:take-over",
    "handoff:claim",
    "handoff:resolve",
    "handoff:handback",
    "handoff:note",
    "decision:rule",
    // Customers' thumbs-up/down on agent replies — reviewing it is desk/quality
    // work, so the Feedback surface lives in the Helpdesk nav section.
    "feedback:read",
    // Workflows (rbac-permissions PR 1): operators run and signal workflows,
    // but never author or publish them. `workflow:read` lets a desk operator
    // see what they run — and lets a `helpdesk_admin` list workflows when
    // scoping a custom role per-resource. Deliberately NO `query:*`: stored
    // queries are a builder/analyst surface with no desk UI, and workflow/agent
    // query execution runs under the node's own scoping (contact RLS), never
    // the operator's grant — so the slugs would be dead weight here.
    "workflow:read",
    "workflow:run",
    "workflow:signal",
    "run:update",
    // Read-only view of the desk roster (helpdesk-team unit) — every operator
    // can SEE the members; only `helpdesk:manage-members` (admin) can change it.
    "helpdesk:read-members",
];
/**
 * The ceiling for HELPDESK-scoped custom roles (helpdesk-team unit): the
 * complete set of slugs a `RoleDefinition` with `scope = 'helpdesk'` may
 * grant. Exactly the `helpdesk_admin` bundle MINUS the `helpdesk:manage-*`
 * delegation slugs — a desk admin can dilute the desk's capabilities into
 * narrower custom roles, but can never author another admin.
 */
export const HELPDESK_ROLE_CEILING = [
    ...HELPDESK_MEMBER_PERMISSIONS,
    "handoff:configure",
    "workflow:cancel",
    // Reply to ANY chat, not just your own claimed ones (desk leads jump into a
    // teammate's chat). `helpdesk_member` deliberately lacks it → own-chats-only;
    // a desk admin can grant it to a custom desk role for reply-all.
    "handoff:reply-all",
];
/**
 * Every slug a HELPDESK principal can ever hold: the ceiling plus the two
 * delegation slugs. A principal whose EVERY permission falls inside this set —
 * and who holds at least one desk-signature slug — is "helpdesk-only": their
 * whole job is the desk, so the dashboard renders a desk-only nav for them
 * (no Platform, no workspace Settings). Admins hold slugs outside this set, so
 * they are never helpdesk-only and see the full nav.
 */
const HELPDESK_PERMISSION_SET = new Set([
    ...HELPDESK_ROLE_CEILING,
    "helpdesk:manage-members",
    "helpdesk:manage-roles",
]);
/**
 * Ambient slugs that EVERY org member carries (via `ORG_ROLE_PERMISSIONS.member`
 * = `org:read`) and that unlock no workspace/org nav surface. They must not
 * disqualify an otherwise desk-only principal — otherwise the check only ever
 * passes for users with no org membership. Org-ADMIN slugs (`org:manage`,
 * `org-member:*`, `billing:*`, `workspace:create`) are deliberately NOT ambient:
 * a holder is a broader admin, not desk-only, and should keep the full nav.
 */
const HELPDESK_AMBIENT_SET = new Set(["org:read"]);
/**
 * Is this principal scoped entirely to the helpdesk? True iff every permission
 * is within {@link HELPDESK_PERMISSION_SET} (ignoring ambient org slugs) AND at
 * least one is a desk-signature slug (`conversation:monitor` / `handoff:*` /
 * `helpdesk:*`) — so a degenerate role holding only `conversation:read` is NOT
 * treated as helpdesk-only.
 */
export function isHelpdeskOnlyPrincipal(permissions) {
    if (permissions.length === 0)
        return false;
    const withinDesk = permissions.every((p) => HELPDESK_PERMISSION_SET.has(p) || HELPDESK_AMBIENT_SET.has(p));
    if (!withinDesk)
        return false;
    return permissions.some((p) => p === "conversation:monitor" || p.startsWith("handoff:") || p.startsWith("helpdesk:"));
}
/**
 * Fine-grained workspace-role bundles (§4.2). `admin` / `member` are the
 * existing bundles, re-exported here so the resolver has every built-in
 * workspace key in one map. The additions reuse catalogue slugs — the B1
 * read-mostly subsets plus the M9 `helpdesk_*` operator roles.
 */
export const WS_ROLE_PERMISSIONS = {
    admin: ROLE_PERMISSIONS.admin,
    member: ROLE_PERMISSIONS.member,
    agent_editor: [
        "workspace:read",
        "agent:read",
        "agent:create",
        "agent:update",
        "agent:delete",
        "kb:read",
        "data-table:read",
        "run:read",
        "conversation:read",
        "feedback:read",
        // Workflows + stored queries (rbac-permissions PR 1): builds and publishes,
        // but does not run-as.
        "workflow:read",
        "workflow:create",
        "workflow:update",
        "workflow:delete",
        "workflow:publish",
        "query:read",
        "query:create",
        "query:update",
        "query:delete",
        "query:run",
    ],
    kb_manager: [
        "workspace:read",
        "kb:read",
        "kb:upload",
        "kb:update",
        "kb:delete",
        "kb:sync",
        "kb:attach",
        "agent:read",
        "run:read",
        "conversation:read",
        "feedback:read",
        // Workflows (rbac-permissions PR 1): read-only visibility.
        "workflow:read",
    ],
    analyst: [
        "workspace:read",
        "member:read",
        "agent:read",
        "kb:read",
        "data-table:read",
        "run:read",
        "conversation:read",
        "handoff:read",
        "feedback:read",
        // Workflows + stored queries (rbac-permissions PR 1): read-only.
        "workflow:read",
        "query:read",
    ],
    // The helpdesk operator: works the handoff queue but configures nothing
    // (Narrative: admin configures, agents operate). No `*:configure`.
    helpdesk_member: HELPDESK_MEMBER_PERMISSIONS,
    // The desk lead: everything `helpdesk_member` has, plus the configure
    // surface (handoff settings, connectors), workflow cancel, and the
    // helpdesk-team delegation slugs (scoped roster + role authoring). The
    // ceiling const IS this bundle minus the delegation slugs — kept as one
    // spread so the two never drift.
    helpdesk_admin: [...HELPDESK_ROLE_CEILING, "helpdesk:manage-members", "helpdesk:manage-roles"],
};
/**
 * The platform-staff bundle. `platform:staff` is both a permission slug and
 * its own role token — cross-tenant support access. Additive: never restricts.
 */
export const PLATFORM_STAFF_PERMISSIONS = ["platform:staff"];
/**
 * Pure resolver: flatten org + workspace + platform role tokens into the
 * effective `permissions[]` (UNION, deduped) plus the namespaced `roles[]`.
 *
 * Org and workspace roles govern different subjects, so union (not
 * max/min/override) is correct. NOTHING calls this yet — Slice 2 wires it in.
 *
 * Tolerant by design: an unknown/legacy workspace-role string (neither a
 * built-in key nor the supplied custom-role id) contributes nothing, and a
 * custom-role slug that has been retired in code is skipped rather than thrown.
 */
export function resolvePrincipalRoles(input) {
    const roles = [];
    const perms = new Set();
    if (input.orgRole && isOrgRole(input.orgRole)) {
        roles.push(`org:${input.orgRole}`);
        for (const p of ORG_ROLE_PERMISSIONS[input.orgRole])
            perms.add(p);
    }
    if (input.workspaceRole) {
        if (isWsBuiltinRole(input.workspaceRole)) {
            roles.push(`ws:${input.workspaceRole}`);
            for (const p of WS_ROLE_PERMISSIONS[input.workspaceRole])
                perms.add(p);
        }
        else if (input.customRole && input.customRole.id === input.workspaceRole) {
            roles.push(`ws:custom:${input.customRole.id}`);
            for (const p of input.customRole.permissions) {
                if (isPermission(p))
                    perms.add(p);
            }
        }
    }
    if (input.platformStaff) {
        roles.push("platform:staff");
        for (const p of PLATFORM_STAFF_PERMISSIONS)
            perms.add(p);
    }
    return { roles, permissions: [...perms] };
}
/**
 * Convenience: union the named built-in role bundles into a flat
 * `permissions[]`. Supports the API-key path (`["ws:admin"]`) and tests.
 * Unknown / custom (`ws:custom:*`) tokens contribute nothing here — custom
 * roles resolve through `resolvePrincipalRoles` with their pre-fetched bundle.
 */
export function permissionsForRoles(roles) {
    const perms = new Set();
    for (const token of roles) {
        if (token.startsWith("org:")) {
            const key = token.slice("org:".length);
            if (isOrgRole(key))
                for (const p of ORG_ROLE_PERMISSIONS[key])
                    perms.add(p);
        }
        else if (token.startsWith("ws:") && !token.startsWith("ws:custom:")) {
            const key = token.slice("ws:".length);
            if (isWsBuiltinRole(key))
                for (const p of WS_ROLE_PERMISSIONS[key])
                    perms.add(p);
        }
        else if (token === "platform:staff") {
            for (const p of PLATFORM_STAFF_PERMISSIONS)
                perms.add(p);
        }
    }
    return [...perms];
}
/**
 * Pure allow-list test for the `platform:staff` bit (§7). `allowList` is the
 * configured set of CometChat internal-staff emails (comma-separated env var,
 * threaded in via the auth config). Comparison is case-insensitive and
 * whitespace-trimmed; an empty/absent list means nobody is staff. This
 * deliberately does NOT reintroduce an `@cometchat.com` email-suffix test — the
 * allow-list is explicit. Migrating to a dedicated table later is a trivial
 * swap (the resolver input is just a boolean).
 */
export function isPlatformStaff(email, allowList) {
    if (!email || allowList.length === 0)
        return false;
    const normalized = email.trim().toLowerCase();
    if (normalized.length === 0)
        return false;
    return allowList.some((entry) => entry.trim().toLowerCase() === normalized);
}
/**
 * Wire-shim derivation (§6.4): the **primary workspace role** — the built-in
 * workspace role (`admin` / `member`) carried in `roles[]`, or `null` when
 * there is no built-in workspace token (workspace-less, custom-role-only, or
 * fine-grained roles that aren't `admin`/`member`).
 *
 * The singular `Principal.role`, the JWT `role` claim, and `AdminRoleGuard`
 * are RETIRED — this survives only to serialize the deprecated `role` field
 * on `/api/me` / login responses for still-cached clients. Delete it (and the
 * wire field) once no cached dashboard bundle can predate the `roles[]` field.
 */
export function primaryWorkspaceRole(roles) {
    const token = roles.find((r) => r.startsWith("ws:") && !r.startsWith("ws:custom:"));
    if (!token)
        return null;
    const key = token.slice("ws:".length);
    return key === "admin" || key === "member" ? key : null;
}
//# sourceMappingURL=roles.js.map